import { ReactNode, useState, memo, useEffect, useMemo } from "react";
import classNames from "classnames";

interface SelectorOption {
  label: string;
  value: string;
  disabled?: boolean;
  disabledText?: string;
  icon?: ReactNode;
}

interface SelectorProps {
  options: SelectorOption[];
  onChange?: (newValue: string) => void;
}

const Selector = ({ options, onChange }: SelectorProps) => {
  const [selectedOption, setSelectedOption] = useState<number>(0);

  const hasDisabledOption = useMemo(() => {
    const firstDisabledOption = options.find((option) => option.disabled);

    if (firstDisabledOption) return true;
    return false;
  }, [options]);

  useEffect(() => {
    const firstEnabledOption = options.findIndex((option) => !option.disabled);

    if (firstEnabledOption >= 0) {
      if (
        onChange &&
        options[firstEnabledOption]?.value &&
        firstEnabledOption !== selectedOption
      ) {
        setSelectedOption(firstEnabledOption);
        onChange(options[firstEnabledOption].value);
      }
    }
  }, [options]);

  return (
    <div
      className={classNames(
        "relative flex min-h-[44px] max-h-[60px] justify-between items-center w-full rounded-full overflow-hidden",
        {
          "bg-[#e9eaed]": hasDisabledOption,
          "bg-primary-900": !hasDisabledOption,
        }
      )}
    >
      {options.map((option, index) => {
        return (
          <div
            key={`selector-option-${index}`}
            onClick={() => {
              if (option.disabled) {
                return;
              }

              setSelectedOption(index);
              if (onChange) onChange(options[index].value);
            }}
            className={classNames(
              "w-full flex flex-col gap-x-2 items-center justify-center h-full px-2 py-2 text-xs md:text-sm font-semibold overflow-hidden text-ellipsis select-none",
              {
                "text-gray-500": hasDisabledOption,
                "text-white": !hasDisabledOption,
                "cursor-not-allowed": option.disabled,
                "cursor-pointer": !option.disabled,
              }
            )}
          >
            <p className="w-full flex gap-x-2 justify-center items-center text-center min-w-max">
              {option?.icon && option?.icon}
              {option?.label}
            </p>
            {option.disabled && option.disabledText ? (
              <p className="w-full font-normal text-[11px] sm:text-xs text-center leading-none">
                {option.disabledText}
              </p>
            ) : null}
          </div>
        );
      })}
      <div
        className={classNames(
          "flex gap-x-2 items-center justify-center absolute transition-all top-0 h-full text-white",
          "rounded-full px-1 bg-primary-500 cursor-default select-none"
        )}
        style={{
          width: `${(100 / options?.length || 1).toFixed(2)}%`,
          left: `${(100 / options?.length || 1) * selectedOption}%`,
        }}
      >
        <div className="w-fit">
          {options[selectedOption]?.icon && options[selectedOption]?.icon}
        </div>
        <p className="w-fit text-xs md:text-sm text-ellipsis font-semibold text-center">
          {options[selectedOption]?.label}
        </p>
      </div>
    </div>
  );
};

export default memo(Selector);
